import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { TableHead, TableWrapper, FilterRow } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { WithWrite } from '../lib/rbac';
import { customersApi, type Customer, type CustomerFormData, type CustomersMeta } from '../services/customers';

const EMPTY_FORM: CustomerFormData = {
  name: '',
  customerTypeItemId: null,
  isCompany: null,
  orgNr: '',
  vatNr: '',
  fortnoxCustomerNumber: '',
  email: '',
  phone: '',
  website: '',
  contactPerson: '',
  invoiceAddress1: '',
  invoiceAddress2: '',
  invoicePostalCode: '',
  invoiceCity: '',
  invoiceCountry: 'Sverige',
  deliveryAddress1: '',
  deliveryAddress2: '',
  deliveryPostalCode: '',
  deliveryCity: '',
  deliveryCountry: 'Sverige',
  notes: '',
  isActive: true,
};

export function CustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [meta, setMeta] = useState<CustomersMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [editing, setEditing] = useState<Customer | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CustomerFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { canRead, canWrite } = useAuth();

  if (!canRead('customers')) {
    return <Navigate to="/" replace />;
  }

  const loadAll = useCallback(async () => {
    try {
      const [customersRes, metaRes] = await Promise.all([customersApi.list(), customersApi.meta()]);
      setCustomers(customersRes.customers);
      setMeta(metaRes);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte hämta kunder');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const resetForm = () => {
    setEditing(null);
    setShowForm(false);
    setForm(EMPTY_FORM);
  };

  const filteredCustomers = useMemo(
    () =>
      customers.filter((customer) => {
        const needle = filter.toLowerCase();
        const matchesText =
          !filter ||
          customer.name.toLowerCase().includes(needle) ||
          (customer.fortnoxCustomerNumber ?? '').toLowerCase().includes(needle) ||
          (customer.contactPerson ?? '').toLowerCase().includes(needle) ||
          (customer.email ?? '').toLowerCase().includes(needle) ||
          String(customer.id).includes(filter);
        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' ? !!customer.isActive : !customer.isActive);
        return matchesText && matchesStatus;
      }),
    [customers, filter, statusFilter]
  );

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = async (customer: Customer) => {
    try {
      const detail = await customersApi.get(customer.id);
      setEditing(detail);
      setForm({
        name: detail.name,
        customerTypeItemId: detail.customerTypeItemId,
        isCompany: detail.isCompany === null ? null : !!detail.isCompany,
        orgNr: detail.orgNr ?? '',
        vatNr: detail.vatNr ?? '',
        fortnoxCustomerNumber: detail.fortnoxCustomerNumber ?? '',
        email: detail.email ?? '',
        phone: detail.phone ?? '',
        website: detail.website ?? '',
        contactPerson: detail.contactPerson ?? '',
        invoiceAddress1: detail.invoiceAddress1 ?? '',
        invoiceAddress2: detail.invoiceAddress2 ?? '',
        invoicePostalCode: detail.invoicePostalCode ?? '',
        invoiceCity: detail.invoiceCity ?? '',
        invoiceCountry: detail.invoiceCountry ?? '',
        deliveryAddress1: detail.deliveryAddress1 ?? '',
        deliveryAddress2: detail.deliveryAddress2 ?? '',
        deliveryPostalCode: detail.deliveryPostalCode ?? '',
        deliveryCity: detail.deliveryCity ?? '',
        deliveryCountry: detail.deliveryCountry ?? '',
        notes: detail.notes ?? '',
        isActive: !!detail.isActive,
      });
      setShowForm(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda kund');
    }
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('Namn krävs');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await customersApi.update(editing.id, form);
        toast.success('Kund uppdaterad');
      } else {
        await customersApi.create(form);
        toast.success('Kund skapad');
      }
      resetForm();
      await loadAll();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara kund');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (customer: Customer) => {
    try {
      await customersApi.delete(customer.id);
      toast.success('Kund inaktiverad');
      if (editing?.id === customer.id) {
        resetForm();
      }
      await loadAll();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte inaktivera kund');
    }
  };

  if (loading || !meta) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Kunder</h1>
        <WithWrite moduleCode="customers">
          <button
            type="button"
            onClick={openCreate}
            className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover"
          >
            Ny kund
          </button>
        </WithWrite>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          placeholder="Filtrera på id, namn, kontakt eller e-post"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          className="w-80"
        />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="w-40">
          <option value="active">Aktiva</option>
          <option value="inactive">Inaktiva</option>
          <option value="all">Alla</option>
        </select>
      </div>

      <TableWrapper>
        <TableHead>
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Id</th>
            <th className="px-2 py-1.5 text-left font-medium">Namn</th>
            <th className="px-2 py-1.5 text-left font-medium">Typ</th>
            <th className="px-2 py-1.5 text-left font-medium">Fortnox</th>
            <th className="px-2 py-1.5 text-left font-medium">Kontakt</th>
            <th className="px-2 py-1.5 text-left font-medium">Telefon</th>
            <th className="px-2 py-1.5 text-left font-medium">E-post</th>
            <th className="px-2 py-1.5 text-left font-medium">Status</th>
            <WithWrite moduleCode="customers">
              <th className="px-2 py-1.5 text-left font-medium">Åtgärder</th>
            </WithWrite>
          </tr>
          <FilterRow>
            <td colSpan={canWrite('customers') ? 9 : 8} className="px-2 py-1" />
          </FilterRow>
        </TableHead>
        <tbody>
          {filteredCustomers.map((customer) => (
            <tr key={customer.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-2 py-1">{customer.id}</td>
              <td className="px-2 py-1 font-medium">{customer.name}</td>
              <td className="px-2 py-1">{customer.customerTypeLabel ?? '-'}</td>
              <td className="px-2 py-1">{customer.fortnoxCustomerNumber ?? '-'}</td>
              <td className="px-2 py-1">{customer.contactPerson ?? '-'}</td>
              <td className="px-2 py-1">{customer.phone ?? '-'}</td>
              <td className="px-2 py-1">{customer.email ?? '-'}</td>
              <td className="px-2 py-1">{customer.isActive ? 'Aktiv' : 'Inaktiv'}</td>
              <WithWrite moduleCode="customers">
                <td className="px-2 py-1">
                  <button type="button" onClick={() => openEdit(customer)} className="mr-2 text-primary hover:underline">
                    Redigera
                  </button>
                  {customer.isActive ? (
                    <button type="button" onClick={() => handleDeactivate(customer)} className="text-red-600 hover:underline">
                      Inaktivera
                    </button>
                  ) : null}
                </td>
              </WithWrite>
            </tr>
          ))}
        </tbody>
      </TableWrapper>

      {filteredCustomers.length === 0 && <p className="text-sm text-gray-500">Inga kunder eller inga träffar.</p>}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <h2 className="text-base font-semibold">{editing ? 'Redigera kund' : 'Ny kund'}</h2>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-sm text-gray-600">Namn *</label>
                  <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Kundtyp</label>
                  <select
                    value={form.customerTypeItemId ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, customerTypeItemId: event.target.value ? Number(event.target.value) : null }))}
                    className="w-full"
                  >
                    <option value="">-</option>
                    {meta.customerTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Typ av part</label>
                  <select
                    value={form.isCompany === null ? '' : form.isCompany ? 'company' : 'private'}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        isCompany: event.target.value === '' ? null : event.target.value === 'company',
                      }))
                    }
                    className="w-full"
                  >
                    <option value="">Okänt</option>
                    <option value="company">Företag</option>
                    <option value="private">Privatperson</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Kontaktperson</label>
                  <input value={form.contactPerson ?? ''} onChange={(event) => setForm((current) => ({ ...current, contactPerson: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Organisationsnummer</label>
                  <input value={form.orgNr ?? ''} onChange={(event) => setForm((current) => ({ ...current, orgNr: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">VAT-nummer</label>
                  <input value={form.vatNr ?? ''} onChange={(event) => setForm((current) => ({ ...current, vatNr: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Fortnox KundID</label>
                  <input value={form.fortnoxCustomerNumber ?? ''} onChange={(event) => setForm((current) => ({ ...current, fortnoxCustomerNumber: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">E-post</label>
                  <input value={form.email ?? ''} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Telefon</label>
                  <input value={form.phone ?? ''} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Webbplats</label>
                  <input value={form.website ?? ''} onChange={(event) => setForm((current) => ({ ...current, website: event.target.value }))} className="w-full" />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="space-y-2 rounded border border-gray-200 p-3">
                  <h3 className="font-medium">Fakturaadress</h3>
                  <input placeholder="Adress 1" value={form.invoiceAddress1 ?? ''} onChange={(event) => setForm((current) => ({ ...current, invoiceAddress1: event.target.value }))} className="w-full" />
                  <input placeholder="Adress 2" value={form.invoiceAddress2 ?? ''} onChange={(event) => setForm((current) => ({ ...current, invoiceAddress2: event.target.value }))} className="w-full" />
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Postnummer" value={form.invoicePostalCode ?? ''} onChange={(event) => setForm((current) => ({ ...current, invoicePostalCode: event.target.value }))} className="w-full" />
                    <input placeholder="Ort" value={form.invoiceCity ?? ''} onChange={(event) => setForm((current) => ({ ...current, invoiceCity: event.target.value }))} className="w-full" />
                  </div>
                  <input placeholder="Land" value={form.invoiceCountry ?? ''} onChange={(event) => setForm((current) => ({ ...current, invoiceCountry: event.target.value }))} className="w-full" />
                </div>
                <div className="space-y-2 rounded border border-gray-200 p-3">
                  <h3 className="font-medium">Leveransadress</h3>
                  <input placeholder="Adress 1" value={form.deliveryAddress1 ?? ''} onChange={(event) => setForm((current) => ({ ...current, deliveryAddress1: event.target.value }))} className="w-full" />
                  <input placeholder="Adress 2" value={form.deliveryAddress2 ?? ''} onChange={(event) => setForm((current) => ({ ...current, deliveryAddress2: event.target.value }))} className="w-full" />
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Postnummer" value={form.deliveryPostalCode ?? ''} onChange={(event) => setForm((current) => ({ ...current, deliveryPostalCode: event.target.value }))} className="w-full" />
                    <input placeholder="Ort" value={form.deliveryCity ?? ''} onChange={(event) => setForm((current) => ({ ...current, deliveryCity: event.target.value }))} className="w-full" />
                  </div>
                  <input placeholder="Land" value={form.deliveryCountry ?? ''} onChange={(event) => setForm((current) => ({ ...current, deliveryCountry: event.target.value }))} className="w-full" />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600">Anteckningar</label>
                <textarea value={form.notes ?? ''} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} className="min-h-[110px] w-full" />
              </div>

              {editing && (
                <label className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm">
                  <input type="checkbox" checked={!!form.isActive} onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))} />
                  Aktiv
                </label>
              )}

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={handleSave} disabled={saving} className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50">
                  {saving ? 'Sparar...' : 'Spara'}
                </button>
                <button type="button" onClick={resetForm} className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100">
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
